import { ICartState } from '@/ui/store/cart/types';
import { Bundle } from '@/ui/services/BundleService';
import { SelectedProductVariants } from '@/ui/components/pages/ProductDetail/types/SelectedProductVariants';
import {
  HardwareProductDetailsVariantFragment,
  RichTextFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { ParsedAccessoryData } from '../components/pages/ProductDetail/AccessoryDetail/types/ParsedAccessoryData';
import { ParsedAccessoryVariant } from '../components/pages/ProductDetail/AccessoryDetail/types/ParsedAccessoryVariant';
import { HardwareProduct } from './HardwareProductService';
import { AccessoryCrystallizeComponents } from '../components/pages/ProductDetail/AccessoryDetail/types/AccessoryCrystallizeComponents';
import { AccessoryCrystallizeData } from '../components/pages/ProductDetail/AccessoryDetail/types/AccessoryCrystallizeData';
import { ParsedShopFeatures } from '../components/pages/ProductDetail/SmartlockDetail/types/ParsedShopFeatures';
import { ProductAlternativeModel } from '../components/pages/ProductDetail/types/ProductAlternativeModel';
import { CylinderDetail } from '../components/pages/ProductDetail/SmartlockDetail/types/CylinderDetail';
import { AttributeTileDetail } from '../components/pages/ProductDetail/types/attribute-tile-detail';

export class Accessory
  extends HardwareProduct<AccessoryCrystallizeData, ParsedAccessoryVariant>
  implements ParsedAccessoryData {
  note?: any;

  components: AccessoryCrystallizeComponents;

  variants: ParsedAccessoryVariant[];

  showOverlay: boolean;

  title?: string;

  cylinderHeading?: RichTextFragment;

  wildcardHeading?: RichTextFragment;

  availableCylinders: CylinderDetail[];

  availableWildcards: AttributeTileDetail[];

  constructor(data: AccessoryCrystallizeData, cartStore: ICartState) {
    super(data, cartStore);
    this.note = this.parseNote(data.components);
    this.components = data.components;
    this.variants = data.variants?.map(this.parseProductVariant) || [];

    this.title = componentByTypename(data.title?.content, 'SingleLineContent')?.text ?? undefined;
    this.cylinderHeading =
      componentByTypename(data.cylinderHeading?.content, 'RichTextContent') ?? undefined;
    this.wildcardHeading =
      componentByTypename(data.wildcardHeading?.content, 'RichTextContent') ?? undefined;
    this.availableCylinders = this.extractUniqueRelationTiles(
      this.variants,
      (variant) => variant.cylinder,
    );
    this.availableWildcards = this.extractUniqueRelationTiles(
      this.variants,
      (variant) => variant.wildcard,
    );

    this.showOverlay =
      componentByTypename(data.showChooser?.content, 'BooleanContent')?.value || false;
  }

  shortDescription?: RichTextFragment | undefined;

  alternativeModelHeading?: RichTextFragment | undefined;

  alternativeModelSection?: ProductAlternativeModel[] | undefined;

  colorHeading?: RichTextFragment | undefined;

  bundlesHeading?: RichTextFragment | undefined;

  shopFeatures?: ParsedShopFeatures;

  addToCart(
    currentVariant: ParsedAccessoryVariant,
    options: {
      selectedBundle?: Bundle;
      selectedBundleProductVariants?: SelectedProductVariants;
      directPurchase?: boolean;
      quantity?: number;
    },
  ): void {
    this.addItemsToCart(currentVariant, options);
    if (options?.directPurchase === true) {
      return;
    }
    this.cartStore.toggleCartDisplay();
  }

  getSelectedVariant(options: {
    color?: string;
    cylinder?: string;
    wildcard?: string;
  }): ParsedAccessoryVariant | undefined {
    // Only constrain on the dimensions that are actually present in the
    // selection. A dimension that is `undefined` does not filter, so products
    // that expose only a subset of color/cylinder/wildcard still resolve to a
    // variant.
    const selectedVariant = this.variants.find(
      (variant) =>
        (options.color === undefined || variant.color?.name === options.color) &&
        (options.cylinder === undefined || variant.cylinder?.type === options.cylinder) &&
        (options.wildcard === undefined || variant.wildcard?.type === options.wildcard),
    );

    return selectedVariant;
  }

  protected parseProductVariant(
    rawVariant: HardwareProductDetailsVariantFragment,
  ): ParsedAccessoryVariant {
    const baseVariant = super.parseProductVariant(rawVariant);
    return {
      ...baseVariant,
      cylinder: this.parseRelationTileDetail(rawVariant.components, 'cylinder'),
      wildcard: this.parseRelationTileDetail(rawVariant.components, 'attribute-wildcard'),
    };
  }

  private parseNote(components: AccessoryCrystallizeData['components']) {
    const noteChunk = componentByTypename(
      components?.find((component) => component.id === 'note')?.content,
      'ContentChunkContent',
    )?.chunks[0];
    if (!noteChunk) {
      return undefined;
    }
    const noteTypeOptions = componentByTypename(
      noteChunk?.find((component) => component.id === 'type')?.content,
      'SelectionContent',
    )?.options;
    return {
      text: componentByTypename(
        noteChunk.find((component) => component.id === 'note')?.content,
        'RichTextContent',
      ),
      type: noteTypeOptions?.[0]?.value,
    };
  }
}
